import { effect } from '@angular/core';

/**
 * Runs `onTransition` (from inside an `effect()`) whenever `source` crosses the given boolean
 * edge: `'rise'` fires on false→true, `'fall'` on true→false.
 *
 * The tracked previous value starts as `false`, deliberately: a source that is already `true` on
 * the effect's first run counts as a rise (a drawer mounted while already open still gets its
 * focus-on-open), while a fall can never fire on the first run (a drawer that is closed at boot
 * must not steal focus onto a button the user never touched).
 *
 * This replaces the hand-rolled `let wasOpen = false; effect(...)` transition-detector idiom that
 * was copy-pasted across the widget surfaces (overlay-drawer focus-in on open, toolbar-button
 * focus-return on close, manage/quick-view mutual exclusivity) — one helper so the next
 * edge-triggered behavior doesn't add a fourth copy.
 *
 * Must be called in an injection context (a constructor or field initializer), like `effect`
 * itself.
 */
export function onBooleanTransition(source: () => boolean, edge: 'rise' | 'fall', onTransition: () => void): void {
  let previous = false;
  effect(() => {
    const current = source();
    const crossed = edge === 'rise' ? current && !previous : !current && previous;
    previous = current;
    if (crossed) onTransition();
  });
}
